import React, {
  useMemo,
  useState,
} from 'react';
import IconButton from '@mui/material/IconButton';
import {
  SidePanelComponentProps,
  Entity,
} from 'pages/Dashboard/contexts/sidePanel';
import ToolTip from 'core/components/Tooltip';
import EditIcon from 'core/icons/edit';
import DeleteIcon from 'core/icons/delete';
import {
  useGetPatientAuthorization,
} from 'views/EMR/components/Authorizations/hooks';
import AuthorizationDialog from 'views/EMR/components/Authorizations/Dialog';
import useAuthorizationDelete from 'views/EMR/components/Authorizations/deleteAction';
import {
  AuthorizationDetailsError,
  AuthorizationDetailsHeader,
  AuthorizationDetailsLoading,
  AuthorizationNotes,
  AuthorizationPayer,
} from 'views/EMR/components/Authorizations/DetailsSections';
import {
  AuthorizationAllowanceTable,
  AuthorizationUsageLedger,
} from 'views/EMR/components/Authorizations/DetailsTables';
import type {
  PatientForm,
} from 'pages/Dashboard/types/patient';

type AuthorizationDetailsEntity = Entity & {
  patient?: PatientForm;
};

type Props = SidePanelComponentProps & {
  entity: AuthorizationDetailsEntity;
};

export default function AuthorizationDetails({ entity, onClose, slots }: Props) {
  const { id, patient } = entity;
  const authorizationId = Number(id);
  const {
    data: authorization,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetPatientAuthorization(authorizationId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const allowances = useMemo(
    () => authorization?.allowances.map((allowance) => ({
      ...allowance,
      remainingQuantity: Math.max(0, allowance.remainingQuantity),
    })) ?? [],
    [authorization?.allowances],
  );
  const usages = useMemo(
    () => allowances.flatMap((allowance) => allowance.usages),
    [allowances],
  );
  const descriptions = useMemo(() => {
    const result: Record<string, string> = {};
    allowances.forEach((allowance) => {
      allowance.codes.forEach((codeEntry) => {
        result[codeEntry.code.toUpperCase()] = codeEntry.description;
      });
    });
    return result;
  }, [allowances]);
  const { requestDelete, isPending: isDeleting } = useAuthorizationDelete({
    onDeleted: onClose,
  });

  const openEdit = () => {
    if (patient) {
      setDialogOpen(true);
    }
  };

  return (
    <>
      {isLoading && <AuthorizationDetailsLoading onClose={onClose} />}
      {!isLoading && (isError || !authorization) && (
        <AuthorizationDetailsError
          error={error}
          onClose={onClose}
          onRetry={() => { refetch(); }}
        />
      )}
      {!isLoading && !isError && authorization && (
        <>
          <div className="flex h-full w-[40rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden">
            <AuthorizationDetailsHeader
              authorization={authorization}
              header={slots?.header}
              actions={(
                <>
                  <ToolTip title="Edit">
                    <span>
                      <IconButton
                        aria-label="Edit authorization"
                        size="small"
                        disabled={!patient}
                        onClick={openEdit}
                      >
                        <EditIcon className="!h-5 !w-5 !text-black" />
                      </IconButton>
                    </span>
                  </ToolTip>
                  <ToolTip title="Delete">
                    <span>
                      <IconButton
                        aria-label="Delete authorization"
                        size="small"
                        disabled={isDeleting}
                        onClick={() => requestDelete(
                          authorizationId,
                          authorization.authorizationNumber,
                        )}
                      >
                        <DeleteIcon className="!h-5 !w-5 !text-black" />
                      </IconButton>
                    </span>
                  </ToolTip>
                </>
              )}
              onClose={onClose}
            />
            <div className="flex min-h-0 flex-1 flex-col gap-6 overflow-auto p-4">
              <AuthorizationPayer authorization={authorization} />
              <AuthorizationAllowanceTable allowances={allowances} />
              <AuthorizationNotes notes={authorization.notes} />
              <AuthorizationUsageLedger usages={usages} descriptions={descriptions} />
            </div>
          </div>
          {patient && (
            <AuthorizationDialog
              patient={patient}
              authorizationId={authorizationId}
              open={dialogOpen}
              onClose={() => { setDialogOpen(false); }}
            />
          )}
        </>
      )}
    </>
  );
}
